import { FC, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import Logo from '../../components/UI/Logo'
import { H2, P } from '../../components/UI/Text'
import Separator from 'views/components/UI/Separator/Index'
import RegisterForm from '../../components/Forms/Register'
import activateUserService from 'services/common/users/activateUser/index'
import { useNotification } from 'lib/context/notification.context'
import { useTranslation } from 'react-i18next'
import Input from 'views/components/UI/TextInput'
import { Button } from 'views/components/UI/Buttons'
import {
  getActionCodeContext,
  resendActivationCode,
  verifyActionCode,
} from 'services/auth/action-code'

const Container = styled.div`
  padding-top: 20px;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media (max-width: 1024px) {
    max-width: 600px;
  }
`
interface IDIV {
  width?: string
  height?: string
  direction?: string
  justify?: string
  align?: string
  bgColor?: string
  padding?: string
  margin?: string
  border?: string
  borderRadius?: string
  boxShadow?: string
  position?: string
  top?: string
  left?: string
  right?: string
  bottom?: string
}

const Div = styled.div<IDIV>`
  width: ${(props) => props.width || '100%'};
  height: ${(props) => props.height || ''};
  display: flex;
  flex-direction: ${(props) => props.direction || 'column'};
  justify-content: ${(props) => props.justify || 'center'};
  align-items: ${(props) => props.align || 'center'};
  background-color: ${(props) => props.bgColor || 'transparent'};
  padding: ${(props) => props.padding || '0'};
  margin: ${(props) => props.margin || '0'};
  border-radius: ${(props) => props.borderRadius || '0'};
  border: ${(props) => props.border || '0'};
  box-shadow: ${(props) => props.boxShadow || '0'};
  position: ${(props) => props.position || 'static'};
  top: ${(props) => props.top || '0'};
  left: ${(props) => props.left || '0'};
  right: ${(props) => props.right || '0'};
  bottom: ${(props) => props.bottom || '0'};
`

interface RegisterFormValues {
  document: string
  birthDate: string
  password: string
  documentType: string
  firstName?: string
  lastName?: string
  role?: string
}

interface ActivationResponse {
  status?: string
  message?: string
  userUpdated?: {
    active?: boolean
  }
}

const RegisterFormContainer: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getWarning, getSuccess } = useNotification()

  const [email, setEmail] = useState<string>('')
  const [code, setCode] = useState<string>('')
  const [isCodeValidated, setCodeValidated] = useState<boolean>(false)
  const [nameUser, setNameUser] = useState<string>('')
  const [lastNameUser, setLastNameUser] = useState<string>('')
  const [storedDocument, setStoredDocument] = useState<string>('')
  const [personId, setPersonId] = useState<number>(0)
  const [isLoading, setLoading] = useState(false)
  const [userRole, setUserRole] = useState<string>('')
  const [isAdminUser, setIsAdminUser] = useState<boolean>(false)
  const [requiresDocument, setRequiresDocument] = useState<boolean>(false)
  const [requiresBirthDate, setRequiresBirthDate] = useState<boolean>(true)
  const [canResendCode, setCanResendCode] = useState<boolean>(false)
  const [isResendingCode, setResendingCode] = useState<boolean>(false)

  const handleValidateCode = async (): Promise<void> => {
    if (!email.trim() || !code.trim()) {
      getWarning('Introduce tu correo y el código recibido.')
      return
    }

    try {
      setLoading(true)
      await verifyActionCode(email.trim(), code.trim(), 'account_activation')
      const response = await getActionCodeContext('account_activation')

      if (response.data.active) {
        getWarning(t('global.notifications.userActiveNoRegister'))
        return
      }

      setNameUser(response.data.first_name)
      setLastNameUser(response.data.last_name)
      setUserRole(response.data.role)
      setPersonId(response.data.personId)
      setStoredDocument(response.data.document)
      setIsAdminUser(['platform_admin', 'super_admin', 'super_platform_admin'].includes(response.data.role ?? ''))
      setRequiresDocument(response.data.requires_document === true)
      setRequiresBirthDate(response.data.requires_birth_date !== false)
      setCodeValidated(true)
      setCanResendCode(false)
    } catch (error: unknown) {
      const responseError = error as { response?: { data?: { message?: string } } }

      setCanResendCode(true)
      getWarning(responseError.response?.data?.message || 'El código es inválido o ha expirado.')
    } finally {
      setLoading(false)
    }
  }

  const handleResendCode = async (): Promise<void> => {
    const normalizedEmail: string = email.trim()

    if (!normalizedEmail) {
      getWarning('Introduce tu correo para solicitar un nuevo código.')
      return
    }

    try {
      setResendingCode(true)
      const response = await resendActivationCode(normalizedEmail)
      getSuccess(response.message || 'Si tu cuenta está pendiente de activación, recibirás un nuevo código en tu correo.')
      setCode('')
      setCanResendCode(false)
    } catch (error: unknown) {
      const responseError = error as { response?: { data?: { message?: string } } }

      getWarning(
        responseError.response?.data?.message ||
          'No fue posible solicitar un nuevo código. Inténtalo de nuevo en unos minutos.'
      )
    } finally {
      setResendingCode(false)
    }
  }

  const handleRegister = async (values: RegisterFormValues) => {
    try {
      const { document, birthDate, password, documentType } = values

      const response: ActivationResponse = await activateUserService({
        document,
        birthDate,
        password,
        documentType: parseInt(documentType),
      })

      const activationConfirmed =
        (response?.status === 'OK' || response?.status === 'success') &&
        response?.userUpdated?.active === true

      if (activationConfirmed) {
        getSuccess(t('global.notifications.createAccountSuccess'))
        setTimeout(() => {
          navigate('/login')
        }, 2000)
      } else {
        getWarning(
          response?.message ||
            'No pudimos confirmar la activación de tu cuenta. Verifica los datos e intenta nuevamente.'
        )
      }
    } catch (err: any) {
      console.error('Error activating user:', err)
      getWarning(err?.message || t('global.notifications.createAccountError'))
    }
  }

  return (
    <Container>
      <Logo width="65%" />
      <Separator height="1rem" />
      <H2 fontSize="32px" margin="10px 0px">
        {t('global.common.welcome')}
      </H2>
      {isCodeValidated ? (
        <Div width="80%" padding="0px 15px 0px 15px" margin="10px 0px 20px 0px">
          <P>
            {isAdminUser ? t('global.register.beforeLoginText') : t('global.register.beforeLoginText2')}
            {t('global.register.beforeLoginText3')}
          </P>
        </Div>
      ) : null}
      {!isCodeValidated ? (
        <Div width="80%" padding="0px 15px" margin="10px 0px 20px 0px">
          <P>Te enviamos un código temporal al correo con el que se creó tu cuenta.</P>
          <Separator height="1rem" />
          <Input
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            type="email"
            name="email"
            label="Correo electrónico"
          />
          <Separator height="1rem" />
          <Input
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            type="text"
            name="activation-code"
            label="Código temporal"
          />
          <Separator height="1rem" />
          <Button disabled={isLoading} onClick={handleValidateCode} type="button">
            {isLoading ? 'Validando código...' : 'Validar código'}
          </Button>
          {canResendCode ? (
            <>
              <Separator height="0.75rem" />
              <P>¿El código ha expirado? Solicita uno nuevo.</P>
              <Button disabled={isResendingCode} onClick={handleResendCode} type="button">
                {isResendingCode ? 'Solicitando código...' : 'Reenviar código'}
              </Button>
            </>
          ) : null}
        </Div>
      ) : isLoading ? (
        <P>Cargando datos del usuario...</P>
      ) : (
        <RegisterForm
          personId={personId}
          storedDocument={storedDocument}
          requireDocument={requiresDocument}
          requireBirthDate={requiresBirthDate}
          defaultValues={{
            firstName: nameUser,
            lastName: lastNameUser,
            role: userRole,
          }}
          onSubmit={(e: any) => handleRegister(e)}
        />
      )}
    </Container>
  )
}

export default RegisterFormContainer
